import React from 'react';
import styled from 'styled-components';
import { FiUser, FiPhone, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import theme from '../../styles/theme';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const AddButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.primary[500]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    border-color: ${theme.colors.primary[600]};
  }
`;

const ContatosListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const ContatoItem = styled.div`
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  position: relative;
`;

const ContatoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[2]};
`;

const ContatoNumber = styled.span`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const RemoveButton = styled.button`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border: 1px solid ${theme.colors.error[500]};
  background: transparent;
  color: ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};

  &:hover {
    background: ${theme.colors.error[500]};
    color: ${theme.colors.text.primary};
  }
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[3]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const EmptyState = styled.div`
  padding: ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  border: 2px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
`;

// Máscara de telefone brasileira
const formatTelefone = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0,2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0,2)}) ${digits.slice(2,6)}-${digits.slice(6)}`;
  return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
};

const ContatosList = ({ contatos, onChange }) => {
  const handleAddContato = () => {
    const novosContatos = [...contatos, { nome: '', telefone: '' }];
    onChange(novosContatos);
  };

  const handleRemoveContato = (index) => {
    const novosContatos = contatos.filter((_, i) => i !== index);
    onChange(novosContatos);
  };

  const handleContatoChange = (index, field, value) => {
    const novosContatos = [...contatos];
    novosContatos[index] = {
      ...novosContatos[index],
      [field]: value
    };
    onChange(novosContatos);
  };

  return (
    <Container>
      <Header>
        <Title>
          <FiUser />
          Contatos
        </Title>
        <AddButton type="button" onClick={handleAddContato}>
          <FiPlus />
          Adicionar Contato
        </AddButton>
      </Header>

      {contatos.length === 0 ? (
        <EmptyState>
          Nenhum contato adicionado. Clique em "Adicionar Contato" para começar.
        </EmptyState>
      ) : (
        <ContatosListContainer>
          {contatos.map((contato, index) => (
            <ContatoItem key={index}>
              <ContatoHeader>
                <ContatoNumber>Contato #{index + 1}</ContatoNumber>
                <RemoveButton 
                  type="button"
                  onClick={() => handleRemoveContato(index)}
                  disabled={contatos.length === 1}
                  title={contatos.length === 1 ? 'É necessário ter pelo menos um contato' : 'Remover contato'}
                >
                  <FiTrash2 />
                  Remover
                </RemoveButton>
              </ContatoHeader>
              <FieldsGrid>
                <FormGroup>
                  <Label htmlFor={`contato-nome-${index}`}>
                    <FiUser />
                    Nome
                  </Label>
                  <Input
                    id={`contato-nome-${index}`}
                    type="text"
                    placeholder="Nome do contato"
                    value={contato.nome || ''}
                    onChange={(e) => handleContatoChange(index, 'nome', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor={`contato-telefone-${index}`}>
                    <FiPhone />
                    Telefone
                  </Label>
                  <Input
                    id={`contato-telefone-${index}`}
                    type="tel"
                    placeholder="(11) 99999-9999"
                    value={contato.telefone || ''}
                    onChange={(e) => handleContatoChange(index, 'telefone', formatTelefone(e.target.value))}
                  />
                </FormGroup>
              </FieldsGrid>
            </ContatoItem>
          ))}
        </ContatosListContainer>
      )}
    </Container>
  );
};

export default ContatosList;

